// Rug Room Shots: Ryan Yaraghi's rug room-swap pipeline. Access is by role: admins/owners
// and anyone whose role or overrides grant the interface key "rug-room-shots" (Photo Department).
// Paste WebIDs -> one ItemID + silo per shape -> Style Sorter picks the style ->
// one of Peter's coded base rooms is chosen -> plan + estimate -> Start (Fast, or
// Half price through Google's Batch API, SPEC 9) -> gemini-3.1-flash-image,
// 4K, 1:1 -> review (silo | base room | result, plus the side-by-side compare view).
//
// The backend (RugRoomShotsController) gates every endpoint to admins/owners or the
// "rug-room-shots" interface key, keeps the global Pause/Resume switch admin/owner only,
// caps non-admins at a daily spend (RugRoomShots:DailyUsdLimitNonAdmin, 409 daily_limit), and
// never calls Gemini; the EC2 worker does the planning and generation by polling
// Aurora. This page only talks to /api/RugRoomShots.
// Contract: EZProcessing/rug-room-shots-worker/SPEC.md sections 3.4 and 5.
//
// URL state: ?tab=new|jobs|review|rooms|spend&job=<id>[&compare=<genId>]
// 2026-10-21 build spec: the effective room-type rules (GET /room-rules, read once and after a change on the
// Rooms tab) ride along as config.roomRules, so the tabs can word "rounds 7R and up use dining rooms".
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Tabs, Tab, Alert, Button, Stack, Chip, Typography, Tooltip, CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { MdPause, MdPlayArrow } from 'react-icons/md';
import InterfaceHeader from '../components/InterfaceHeader';
import { usePermissions } from '../context/PermissionContext';
import rrsApi from './rug-room-shots/api';
import {
  AI_COLOR, DEFAULT_CONFIG, fmtNy, fmtNyTime, tsMs, fmtInt, fmtDuration, fmtUsd, usePolling, ConfirmDialog,
  FORBIDDEN_TEXT, budgetFromConfig, RRS_SPEND_EVENT,
} from './rug-room-shots/common';
import NewJobTab from './rug-room-shots/NewJobTab';
import JobsTab from './rug-room-shots/JobsTab';
import ReviewTab from './rug-room-shots/ReviewTab';
import RoomsTab from './rug-room-shots/RoomsTab';
import SpendLogTab from './rug-room-shots/SpendLogTab';

// Dispatch blocks the worker sets itself (rrs/generator.py local guards): nothing new starts
// until they clear; images already generating finish.
const LOCAL_BLOCKS = {
  disk_low: 'New generations are on hold: the worker host is low on disk space.',
  upload_backlog: 'New generations are on hold: finished images are waiting to be uploaded to S3.',
  upload_failing: 'New generations are on hold: uploads to S3 keep failing (finished images are kept on the worker).',
};

const TABS = [
  { key: 'new', label: 'New job' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'review', label: 'Review' },
  { key: 'rooms', label: 'Rooms' },
  { key: 'spend', label: 'Spend & log' },
];
const TAB_KEYS = TABS.map((t) => t.key);
const FORBIDDEN = FORBIDDEN_TEXT;
const PAUSE_ADMIN_ONLY = 'You do not have permission to pause or resume generation - ask an administrator to add Rug Room Shots to your role.';

// Non-admins: "Today: $X of $1,000 used - $Y left" (New York day). Hidden for admins / owners.
const BudgetChip = ({ budget }) => {
  if (!budget || !budget.limited) return null;
  const spent = budget.spentTodayUsd || 0;
  const left = budget.remainingUsd !== null ? budget.remainingUsd : Math.max(0, budget.limitUsd - spent);
  const pct = budget.limitUsd > 0 ? spent / budget.limitUsd : 0;
  const color = left <= 0 ? 'error' : (pct >= 0.8 ? 'warning' : 'default');
  return (
    <Tooltip title="Your spending on Rug Room Shots today (New York day), counting queued images at their estimated price. The limit resets at midnight New York time. Administrators have no limit.">
      <Chip
        size="small" variant="outlined" color={color}
        label={`Today: ${fmtUsd(spent)} of ${fmtUsd(budget.limitUsd, 0)} used · ${fmtUsd(left)} left`}
      />
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Health banner (all tabs): worker offline / paused / Gemini blocked / backoff.
// ---------------------------------------------------------------------------
const HealthBanner = ({ health, healthError, onPause, onResume, pauseBusy, canPause, budget }) => {
  if (healthError) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="warning" sx={{ mb: budget && budget.limited ? 1 : 0 }}>Worker status unavailable: {healthError}</Alert>
        <BudgetChip budget={budget} />
      </Box>
    );
  }
  if (!health) return budget && budget.limited ? <Box sx={{ mb: 2 }}><BudgetChip budget={budget} /></Box> : null;
  const w = health.worker || null;
  const online = !!(w && w.online);
  const backoffMs = w && w.backoffUntil ? tsMs(w.backoffUntil) : null;
  const inBackoff = backoffMs && backoffMs > Date.now();
  const q = health.queue || {};
  const pausedBy = health.generationPausedBy || health.pausedBy || null;
  // Half-price batches (SPEC 9.4 / 9.7 / 9.10): GET /health "batches" and queue batched / submitted.
  const b = health.batches || q.batches || {};
  const atGoogle = (q.batched || 0) + (q.submitted || 0);
  const oldestMs = tsMs(b.oldestSubmittedAt);
  const unrecognised = Array.isArray(b.unrecognisedNames) ? b.unrecognisedNames.filter(Boolean).map(String) : [];
  const unrecognisedCount = Math.max(Number(b.unrecognised) || 0, unrecognised.length);
  const overdueList = Array.isArray(b.overdueBatches) ? b.overdueBatches : [];
  const overdueCount = Math.max(Number(b.overdue) || 0, overdueList.length);
  const resultsOverdueList = Array.isArray(b.resultsOverdueBatches) ? b.resultsOverdueBatches : [];
  const resultsOverdue = Math.max(Number(b.resultsOverdue) || 0, resultsOverdueList.length);

  return (
    <Box sx={{ mb: 2 }}>
      {!online && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {w && w.lastSeenAt
            ? <>Worker offline since {fmtNy(w.lastSeenAt)} (New York). Plans and generations wait until it is back.</>
            : <>The worker has not reported yet. Plans and generations wait until it starts.</>}
        </Alert>
      )}
      {health.generationPaused && (
        <Alert
          severity="warning" sx={{ mb: 1 }}
          action={canPause ? (
            <Button color="inherit" size="small" startIcon={<MdPlayArrow />} onClick={onResume} disabled={pauseBusy}>
              Resume
            </Button>
          ) : undefined}
        >
          Generation paused{pausedBy ? ` by ${pausedBy}` : ''}. Images already generating finish; nothing new starts.
          {!canPause && ' Queued images wait until an administrator resumes it.'}
        </Alert>
      )}
      {w && w.dispatchBlocked && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {LOCAL_BLOCKS[w.dispatchBlocked]
            || `Gemini unavailable: ${String(w.dispatchBlocked).replace(/_/g, ' ')} (retrying).`}
        </Alert>
      )}
      {overdueCount > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {overdueList.length
            ? overdueList.map((o) => (
              <Box key={o.batchId || `${o.jobId}-${o.submittedAt}`}>
                A half-price batch for job #{o.jobId} has been at Google for {o.submittedAt ? fmtDuration(Date.now() - tsMs(o.submittedAt)) : `${o.hoursAtGoogle} h`} (Google gives up at 48 h).
              </Box>
            ))
            : (
              <>
                {overdueCount === 1 ? 'A half-price batch has' : `${fmtInt(overdueCount)} half-price batches have`} been at Google for more than 24 h
                {oldestMs ? ` (the oldest for ${fmtDuration(Date.now() - oldestMs)})` : ''}. Google gives up at 48 h; the worker keeps checking.
              </>
            )}
        </Alert>
      )}
      {resultsOverdue > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Google finished {resultsOverdue === 1 ? 'a half-price batch' : `${fmtInt(resultsOverdue)} half-price batches`} more than 24 h ago, but the results are not downloaded yet
          {resultsOverdueList.length ? ` (job${resultsOverdueList.length === 1 ? '' : 's'} ${[...new Set(resultsOverdueList.map((x) => `#${x.jobId}`))].join(', ')})` : ''}.
          The worker keeps retrying; Google keeps results for 6 weeks.
        </Alert>
      )}
      {unrecognisedCount > 0 && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {unrecognised.length
            ? unrecognised.map((n) => <Box key={n}>Unrecognised Google batch: {n}</Box>)
            : `${fmtInt(unrecognisedCount)} unrecognised Google batch(es) (see the worker log).`}
        </Alert>
      )}
      {inBackoff && (
        <Alert severity="info" icon={false} sx={{ mb: 1, bgcolor: 'action.hover', color: 'text.primary' }}>
          Rate limited by Google until {fmtNyTime(w.backoffUntil)} (New York).
        </Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Tooltip title={w && w.lastSeenAt ? `Last heartbeat ${fmtNy(w.lastSeenAt)}${w.workerId ? ` · ${w.workerId}` : ''}${w.version ? ` · ${w.version}` : ''}` : ''}>
          <Chip size="small" color={online ? 'success' : 'error'} variant="outlined" label={online ? 'Worker online' : 'Worker offline'} />
        </Tooltip>
        {w && w.lookupBackend && <Chip size="small" variant="outlined" label={`Lookups: ${w.lookupBackend}`} />}
        {w && w.concurrency ? <Chip size="small" variant="outlined" label={`${w.inflight || 0} of ${w.concurrency} slots busy`} /> : null}
        <Chip size="small" variant="outlined" label={`Queue: ${fmtInt(q.queued || 0)} queued · ${fmtInt(q.running || 0)} generating`} />
        {atGoogle > 0 && (
          <Tooltip title={oldestMs ? `Oldest batch sent ${fmtDuration(Date.now() - oldestMs)} ago. Google usually returns within hours, up to 24 h.` : 'Google usually returns within hours, up to 24 h.'}>
            <Chip
              size="small" variant="outlined" color="secondary"
              label={`Half price: ${fmtInt(atGoogle)} at Google${b.atGoogle ? ` in ${fmtInt(b.atGoogle)} batch${b.atGoogle === 1 ? '' : 'es'}` : ''}`}
            />
          </Tooltip>
        )}
        <BudgetChip budget={budget} />
        <Box sx={{ flex: 1 }} />
        {canPause && !health.generationPaused && (
          <Tooltip title="Operational switch: stops new generations from starting (running ones finish). Not a spending cap.">
            <span>
              <Button size="small" color="warning" startIcon={<MdPause />} onClick={onPause} disabled={pauseBusy}>
                Pause generation
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
const RugRoomShots = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin, isOwner, hasAccessToPath, loading: permLoading } = usePermissions();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab = TAB_KEYS.includes(rawTab) ? rawTab : 'new';
  const jobId = parseInt(params.get('job') || '', 10) || null;

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [forbidden, setForbidden] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState('');
  const [pauseBusy, setPauseBusy] = useState(false);
  const [pauseConfirm, setPauseConfirm] = useState(false);
  const [roomRules, setRoomRules] = useState(null);
  // Pause / Resume is open to everyone with access (owner decision 2026-10-12); the backend's /config canPause decides.
  const budget = budgetFromConfig(config);
  const canPause = config.canPause !== false;

  const notify = useCallback((message, variant = 'info') => {
    enqueueSnackbar(message, { variant });
  }, [enqueueSnackbar]);

  const go = useCallback((nextTab, nextJob, opts) => {
    const sp = new URLSearchParams();
    sp.set('tab', nextTab);
    if (nextJob) sp.set('job', String(nextJob));
    setParams(sp, opts);
  }, [setParams]);

  // /config is re-read every minute: "speeds[].available" follows the worker's heartbeat.
  // "features" (Add room / Add style, SPEC 10) also follows it and is kept by the spread below;
  // tabs read it as (config.features || {}). The Rooms tab re-reads /config after Add style.
  // The state only changes when the answer changes, so the tabs do not re-render for nothing.
  const lastConfigJson = useRef('');
  const loadConfig = useCallback(async () => {
    try {
      const c = await rrsApi.config();
      const json = JSON.stringify(c || {});
      if (json === lastConfigJson.current) return;
      lastConfigJson.current = json;
      setConfig({
        ...DEFAULT_CONFIG,
        ...c,
        styles: (c && c.styles && c.styles.length) ? c.styles : DEFAULT_CONFIG.styles,
        shapes: (c && c.shapes && c.shapes.length) ? c.shapes : DEFAULT_CONFIG.shapes,
        rejectReasons: (c && c.rejectReasons && c.rejectReasons.length) ? c.rejectReasons : DEFAULT_CONFIG.rejectReasons,
      });
    } catch (e) {
      if (e.status === 403) setForbidden(true);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const h = await rrsApi.health();
      setHealth(h);
      setHealthError('');
    } catch (e) {
      if (e.status === 403) setForbidden(true);
      else setHealthError(e.message || 'request failed');
    }
  }, []);

  // Room-type rules for the wording of warnings; an older backend has no endpoint (the page then uses the defaults).
  const loadRoomRules = useCallback(async () => {
    try {
      const r = await rrsApi.getRoomRules();
      if (r && typeof r === 'object') setRoomRules(r);
    } catch (e) {
      /* 404 on an older backend, or no access: the code defaults are used */
    }
  }, []);
  const onRoomRulesChanged = useCallback((r) => {
    if (r && typeof r === 'object') setRoomRules(r);
    else loadRoomRules();
  }, [loadRoomRules]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => { loadRoomRules(); }, [loadRoomRules]);
  const pageConfig = useMemo(() => (roomRules ? { ...config, roomRules } : config), [config, roomRules]);
  // Start / Generate remaining / Regenerate / bulk regenerate (or a daily_limit refusal)
  // signal here; re-read /config so the daily budget indicator is current.
  useEffect(() => {
    const onSpend = () => { loadConfig(); };
    window.addEventListener(RRS_SPEND_EVENT, onSpend);
    return () => window.removeEventListener(RRS_SPEND_EVENT, onSpend);
  }, [loadConfig]);
  usePolling(loadHealth, 30000, !forbidden);
  usePolling(loadConfig, 60000, !forbidden);

  const setPaused = async (paused) => {
    if (!canPause) {
      notify(PAUSE_ADMIN_ONLY, 'error');
      return;
    }
    setPauseBusy(true);
    try {
      await rrsApi.setPaused(paused);
      notify(paused ? 'Generation paused. Running images finish; nothing new starts.' : 'Generation resumed.', paused ? 'warning' : 'success');
      setPauseConfirm(false);
      await loadHealth();
    } catch (e) {
      notify(e.status === 403 ? PAUSE_ADMIN_ONLY : (e.message || 'Request failed'), 'error');
    } finally {
      setPauseBusy(false);
    }
  };

  // Navigation callbacks handed to the tabs (stable identities).
  const openJobPlan = useCallback((id) => go('new', id), [go]);
  const newJob = useCallback(() => go('new', null), [go]);
  const goReview = useCallback((id) => go('review', id), [go]);
  const reviewJobChange = useCallback((id, opts) => go('review', id, opts), [go]);

  // Access: admins / owners, or the "rug-room-shots" interface key (Photo Department role).
  // The route guard already enforces this; the backend is the real gate (403 -> forbidden).
  const noAccess = !permLoading && !isAdmin && !isOwner && !hasAccessToPath('rug-room-shots');

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1600, mx: 'auto' }}>
      <InterfaceHeader title="Rug Room Shots" color={AI_COLOR} category="ai" page="RugRoomShots" />
      <Typography variant="body2" color="text.secondary" sx={{ mt: -2, mb: 2, textAlign: 'center' }}>
        Paste WebIDs, check the plan and the estimate, click Start, then review each room shot.
        {budget.limited ? ` Limit: ${fmtUsd(budget.limitUsd, 0)} per person per day (New York).` : ''}
      </Typography>

      {(forbidden || noAccess) ? (
        <Alert severity="error">{FORBIDDEN}</Alert>
      ) : (
        <>
          <HealthBanner
            health={health} healthError={healthError} pauseBusy={pauseBusy}
            canPause={canPause} budget={budget}
            onPause={() => setPauseConfirm(true)} onResume={() => setPaused(false)}
          />

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(e, v) => go(v, jobId)}
              variant="scrollable" scrollButtons="auto"
              sx={{ '& .MuiTabs-indicator': { bgcolor: AI_COLOR }, '& .Mui-selected': { color: `${AI_COLOR} !important` } }}
            >
              {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} sx={{ textTransform: 'none', fontWeight: 600 }} />)}
            </Tabs>
          </Box>

          {/* Only the visible tab is mounted, so hidden tabs never poll. */}
          {tab === 'new' && (
            <NewJobTab
              config={pageConfig} jobId={jobId} health={health} notify={notify}
              onOpenJob={openJobPlan} onNewJob={newJob} onGoReview={goReview}
            />
          )}
          {tab === 'jobs' && <JobsTab config={pageConfig} health={health} notify={notify} onOpenPlan={openJobPlan} onOpenReview={goReview} />}
          {tab === 'review' && <ReviewTab config={pageConfig} health={health} jobId={jobId} onJobChange={reviewJobChange} notify={notify} />}
          {tab === 'rooms' && (
            <RoomsTab
              config={pageConfig} notify={notify} health={health} onHealthRefresh={loadHealth} onConfigRefresh={loadConfig}
              onRoomRulesChanged={onRoomRulesChanged}
            />
          )}
          {tab === 'spend' && <SpendLogTab config={pageConfig} />}
        </>
      )}

      {permLoading && !forbidden && (
        <Box sx={{ position: 'fixed', top: 12, right: 12 }}><CircularProgress size={18} /></Box>
      )}

      <ConfirmDialog
        open={pauseConfirm && canPause} busy={pauseBusy} onClose={() => setPauseConfirm(false)} onConfirm={() => setPaused(true)}
        title="Pause generation?" confirmLabel="Pause" confirmColor="warning"
      >
        <Typography>
          The worker stops starting new images for everyone. Images already generating finish and are recorded.
          Queued images stay queued until you resume.
        </Typography>
      </ConfirmDialog>
    </Box>
  );
};

export default RugRoomShots;
